import axios from "axios";
import { useState } from "react";
import {
  Copy,
  CheckCircle2,
  Share2,
  ExternalLink,
  Globe,
  Loader2,
  QrCode,
  Download,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API } from "@/context/AuthContext";

// Public links use the current browser origin so they keep working
// when the app is deployed under a custom domain.
const PUBLIC_BASE = typeof window !== "undefined" ? window.location.origin : "";

/**
 * Panel shown on the job detail page with:
 * 1) Public careers link (sharable)
 * 2) Google X-ray search (works without login on any platform)
 * 3) Direct recruiter login links (login required, no URL params possible)
 */
const JobShareAndSearchPanel = ({ job, companySlug, orgSlug, onJobUpdated }) => {
  const [copied, setCopied] = useState(false);
  // Prefer the Organization-scoped link when the job is linked to an
  // Organization (the correct "employer" for the candidate view). Falls
  // back to the legacy user-tenant link.
  const publicUrl = job?.slug
    ? orgSlug
      ? `${PUBLIC_BASE}/c/org/${orgSlug}/${job.slug}`
      : companySlug
      ? `${PUBLIC_BASE}/c/${companySlug}/${job.slug}`
      : ""
    : "";
  // Link otimizado para compartilhamento social (OG meta tags + redirect).
  // Usado apenas quando há uma Organization — o backend só tem endpoint
  // /share para orgs (não para o tenant legado).
  const shareUrl = job?.slug && orgSlug
    ? `${PUBLIC_BASE}/api/public/share/orgs/${orgSlug}/jobs/${job.slug}`
    : publicUrl;

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(
        orgSlug
          ? "Link copiado! Preview rico no WhatsApp/LinkedIn."
          : "Link copiado!"
      );
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const handleShareNative = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: job.title,
          text: `Vaga aberta: ${job.title}${
            job.location ? ` - ${job.location}` : ""
          }`,
          url: shareUrl,
        });
      } catch {
        // user cancelled
      }
    } else {
      handleCopy();
    }
  };

  // ─── QR Code ─────────────────────────────────────────────────────
  const [qrBlobUrl, setQrBlobUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");

  const loadQrCode = async () => {
    if (qrBlobUrl || qrLoading || !publicUrl) return;
    setQrLoading(true);
    setQrError("");
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/qrcode.png?size=512`, {
        responseType: "blob",
      });
      setQrBlobUrl(URL.createObjectURL(res.data));
    } catch (err) {
      setQrError(err?.response?.data?.detail || "Não foi possível gerar o QR Code");
    } finally {
      setQrLoading(false);
    }
  };

  const handleDownloadQr = async () => {
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/qrcode.png?size=1024`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const slug = (job?.slug || job?.id || "vaga").slice(0, 60);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qrcode-vaga-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 100);
      toast.success("QR Code baixado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao baixar QR Code");
    }
  };

  const handlePrintQr = () => {
    if (!qrBlobUrl) return;
    const w = window.open("", "_blank", "width=600,height=800");
    if (!w) {
      toast.error("Permita pop-ups para imprimir");
      return;
    }
    const title = (job?.title || "Vaga").replace(/</g, "&lt;");
    const loc = job?.location ? `${job.location}` : "";
    w.document.write(`<!doctype html><html><head><title>QR ${title}</title>
<style>
  body { font-family: -apple-system, system-ui, sans-serif; margin: 0; padding: 32px; text-align: center; color: #0f172a; }
  h1 { font-size: 22px; margin: 0 0 4px; font-weight: 700; }
  .sub { font-size: 14px; color: #475569; margin-bottom: 28px; }
  img { width: 380px; height: 380px; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; background: white; }
  .url { font-family: monospace; font-size: 11px; color: #475569; margin-top: 20px; word-break: break-all; padding: 0 24px; }
  .cta { font-size: 13px; color: #1e293b; margin-top: 18px; font-weight: 600; }
  @media print { body { padding: 16px; } img { width: 320px; height: 320px; } }
</style></head><body>
  <h1>${title}</h1>
  <div class="sub">${loc}</div>
  <img src="${qrBlobUrl}" alt="QR Code" />
  <div class="cta">📱 Escaneie e candidate-se</div>
  <div class="url">${publicUrl}</div>
  <script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`);
    w.document.close();
  };

  return (
    <div
      data-testid="share-and-search-panel"
      className="bg-white border border-slate-200 rounded-lg p-5 space-y-5"
    >
      {/* Public link */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-slate-700" />
          <h3 className="heading text-sm font-semibold text-slate-900">
            Página pública desta vaga
          </h3>
        </div>
        <p className="text-xs text-slate-600 mb-3 leading-relaxed">
          Compartilhe este link em LinkedIn, WhatsApp ou cole no anúncio da Catho.
          Cada candidato pode aplicar diretamente — a IA já analisa.
        </p>
        {publicUrl ? (
          <>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mb-2">
              <code
                className="flex-1 text-xs text-slate-700 font-mono truncate"
                data-testid="public-job-url"
              >
                {publicUrl}
              </code>
              <Button
                data-testid="copy-public-url"
                size="sm"
                variant="outline"
                onClick={handleCopy}
                className="shrink-0 h-8"
              >
                {copied ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="open-public-page"
                variant="outline"
                size="sm"
                onClick={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}
                className="h-8 text-xs"
              >
                <ExternalLink className="w-3 h-3 mr-1.5" />
                Abrir página
              </Button>
              <Button
                data-testid="share-public-page"
                variant="outline"
                size="sm"
                onClick={handleShareNative}
                className="h-8 text-xs"
              >
                <Share2 className="w-3 h-3 mr-1.5" />
                Compartilhar
              </Button>
            </div>
          </>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            Configure o slug da empresa em <strong>Configurações → Empresa</strong> para
            gerar o link público.
          </div>
        )}
      </section>

      {/* QR Code — captação offline (eventos, flyers, feiras) */}
      {publicUrl && (
        <section
          className="pt-5 border-t border-slate-100"
          data-testid="qrcode-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <QrCode className="w-4 h-4 text-violet-700" />
            <h3 className="heading text-sm font-semibold text-slate-900">
              QR Code da vaga{" "}
              <span className="text-violet-700">(captação offline)</span>
            </h3>
          </div>
          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            Imprima e leve pra eventos, feiras, universidades ou cole em flyers
            no escritório. Candidato escaneia com a câmera e cai direto na
            página pública — sem cadastro, sem digitação.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div
              className="bg-white border border-slate-200 rounded-lg p-3 shrink-0 cursor-pointer hover:border-violet-300 transition-colors"
              onClick={loadQrCode}
              data-testid="qrcode-preview"
            >
              {qrLoading ? (
                <div className="w-32 h-32 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                </div>
              ) : qrBlobUrl ? (
                <img
                  src={qrBlobUrl}
                  alt={`QR Code para ${job.title}`}
                  className="w-32 h-32 block"
                  data-testid="qrcode-image"
                />
              ) : qrError ? (
                <div className="w-32 h-32 flex items-center justify-center text-[10px] text-rose-600 text-center px-2">
                  {qrError}
                </div>
              ) : (
                <div className="w-32 h-32 flex flex-col items-center justify-center gap-1 text-slate-400">
                  <QrCode className="w-10 h-10" />
                  <span className="text-[10px]">Clique para gerar</span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 flex-1 min-w-0 w-full">
              <Button
                data-testid="qrcode-download"
                size="sm"
                variant="outline"
                onClick={handleDownloadQr}
                disabled={!publicUrl}
                className="h-8 text-xs justify-start"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Baixar PNG (1024px)
              </Button>
              <Button
                data-testid="qrcode-print"
                size="sm"
                variant="outline"
                onClick={() => {
                  if (!qrBlobUrl) loadQrCode().then(handlePrintQr);
                  else handlePrintQr();
                }}
                disabled={!publicUrl}
                className="h-8 text-xs justify-start"
              >
                <Printer className="w-3.5 h-3.5 mr-1.5" />
                Imprimir com título
              </Button>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                Aponta para a página pública desta vaga. Teste com a câmera do
                seu celular antes de imprimir em massa.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Catho custom search panel — removed: feature not used in practice */}

      {/* Direct recruiter logins — removed: not useful in practice */}
    </div>
  );
};

export default JobShareAndSearchPanel;
